import { Checkbox, Col, Row } from 'antd';
import React, { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Typography } from 'tfc-components';

import { PackageForm, PackageOption, IPackage } from '.';

import Icon from 'components/atoms/Icon';
import Loading from 'components/atoms/Loading';
import mapModifiers from 'utils/functions';

interface PackageSelectorProps {
  mainPackages: PackageOption[];
  duration: 'monthly' | 'yearly';
  setDuration: (duration: 'monthly' | 'yearly') => void;
  selectedMain: IPackage;
  selectedAddons: IPackage[];
  setSelectedMain: (data: IPackage) => void;
  setSelectedAddons: (data: IPackage | null) => void;
  isMainPackageLoading: boolean;
  isAddonPackageLoading: boolean;
  isGoBack: boolean;
  setIsGoBack: (isGoBack: boolean) => void;
  method: UseFormReturn<PackageForm>;
}

const PackageSelector: React.FC<PackageSelectorProps> = ({
  duration,
  setDuration,
  selectedMain,
  selectedAddons,
  setSelectedMain,
  setSelectedAddons,
  mainPackages,
  isMainPackageLoading,
  isAddonPackageLoading,
  isGoBack,
  setIsGoBack,
  method
}) => {
  useEffect(() => {
    if (!isGoBack) {
      setSelectedMain({
        id: '',
        name: '',
        quantity: 1,
        duration: 1,
        price: 0,
        latestTier: {
          numberOfAgent: 0,
          numberOfFile: 0,
          numberOfSession: 0,
          numberOfWebsite: 0,
          totalSizeLimit: 0
        }
      });
      setSelectedAddons(null);
    }
  }, [duration, isGoBack]);

  const addOnPackages = mainPackages.find(
    (pkg) => pkg?.id === selectedMain?.id
  )?.servicePackageAddOnData?.map((item) => ({
    ...item,
    latestTier: {
      numberOfAgent: item?.numberOfAgent,
      numberOfFile: item?.numberOfFile,
      numberOfSession: item?.numberOfSession,
      numberOfWebsite: item?.numberOfWebsite,
      totalSizeLimit: item?.totalSizeLimit,
    }
  })) || [];

  const toggleAddon = (data: IPackage) => {
    setSelectedAddons(data);
  };

  const handleSelectPackage = (data: IPackage) => {
    setSelectedMain(data);
    method.setValue('addOns', [], { shouldDirty: true, shouldValidate: true });
  };

  return (
    <div>
      <div className="p-checkout_durationTabs">
        <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct fs-20x24">Select a Package</Typography.Text>
        <Row className="p-checkout_durationTabs_container" gutter={[32, 16]}>
          <Col span={4}>
            <Row
              onClick={() => {
                setDuration('yearly');
                method.setValue('addOns', [], { shouldDirty: true, shouldValidate: true });
                setIsGoBack(false);
              }}
            >
              <Typography.Text
                extendClasses={mapModifiers('fs-16x24 p-checkout_durationTabs_button', duration === 'yearly' && 'active')}
                fontweight="600"
              >
                Yearly
              </Typography.Text>
            </Row>
            <Row
              onClick={() => {
                setDuration('monthly');
                setIsGoBack(false);
                method.setValue('addOns', [], { shouldDirty: true, shouldValidate: true });
              }}
            >
              <Typography.Text
                fontweight="600"
                extendClasses={mapModifiers('fs-16x24 p-checkout_durationTabs_button', duration === 'monthly' && 'active')}
              >
                Monthly
              </Typography.Text>
            </Row>
          </Col>
          <Col span={20}>
            <Row>
              <div className="p-checkout_mainPackage">
                <div className="p-checkout_mainPackage_title">
                  <Typography.Text fontweight="600" extendClasses="p-checkout_mainPackage_titleText fs-16x20">Main package</Typography.Text>
                  <div className="p-checkout_mainPackage_titleDivider" />
                </div>
                {isMainPackageLoading ? <div className="p-checkout_mainPackage"><Loading /></div> : (
                  <div className="p-checkout_grid">
                    {mainPackages.map((pkg) => (
                      <div
                        key={pkg?.id}
                        className={`p-checkout_packageCard ${selectedMain?.id === pkg?.id ? 'selected' : ''}`}
                        onClick={() => handleSelectPackage({
                          id: pkg?.id,
                          duration: 1,
                          name: pkg?.name,
                          price: pkg?.amount,
                          quantity: 1,
                          latestTier: pkg?.latestTier
                        })}
                      >
                        <div className="p-checkout_packageCard_info">
                          <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_name fs-18x21">{pkg?.name}</Typography.Text>
                          <div className="p-checkout_packageCard_price">
                            <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_amount fs-18x21">
                              {pkg?.amount.toLocaleString('vi')}
                              {' '}
                              VND
                            </Typography.Text>
                            <span className="p-checkout_packageCard_duration">
                              /
                              {duration === 'monthly' ? 'month' : 'year'}
                            </span>
                          </div>
                        </div>
                        {selectedMain?.id === pkg?.id && (
                          <div className="p-checkout_packageCard_check">
                            <Icon iconName="checkWhite" size="24" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </Row>

            <Row>
              <div className="p-checkout_addonPackage">
                <div className="p-checkout_mainPackage_title">
                  <Typography.Text fontweight="600" extendClasses="p-checkout_mainPackage_titleText fs-16x20">Add-on package</Typography.Text>
                  <div className="p-checkout_mainPackage_titleDivider" />
                </div>
                {isAddonPackageLoading ? <div className="p-checkout_mainPackage"><Loading /></div> : (
                  <div className="p-checkout_grid">
                    {addOnPackages.length > 0 ? addOnPackages.map((pkg) => (
                      <div
                        key={pkg?.id}
                        className={`p-checkout_packageCard p-checkout_packageCard_addon${selectedAddons.some((a) => a.packageId === `${pkg?.id}`)
                          ? 'Selected' : ''}`}
                        onClick={() => toggleAddon({
                          id: `${pkg?.id}`,
                          name: pkg?.name,
                          duration: 1,
                          quantity: 1,
                          price: pkg.price,
                          latestTier: pkg.latestTier
                        })}
                      >
                        <div className="p-checkout_packageCard_addOnInfo">
                          <div>
                            <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_name fs-18x21">{pkg?.name}</Typography.Text>
                            <div className="p-checkout_packageCard_price">
                              <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_amount fs-18x21">
                                {pkg?.price.toLocaleString('vi')}
                                {' '}
                                VND
                              </Typography.Text>
                              <span className="p-checkout_packageCard_duration">
                                /
                                {duration === 'monthly' ? 'month' : 'year'}
                              </span>
                            </div>
                          </div>
                          <Checkbox
                            type="checkbox"
                            checked={selectedAddons.some((a) => a.packageId === `${pkg?.id}`)}
                            className="p-monitorDashboard_filter_optionCheckbox"
                          // onChange
                          />
                        </div>
                        {pkg?.description && (
                          <div className="p-checkout_packageCard_hover">
                            <Typography.Text extendClasses="fs-14x18">
                              {pkg?.description}
                            </Typography.Text>
                          </div>
                        )}
                      </div>
                    ))
                      : (
                        <div className="p-checkout_packageCard_empty">
                          <Typography.Text fontweight="600" extendClasses="p-checkout_packageCard_name">No Available Package</Typography.Text>
                        </div>
                      )}
                  </div>
                )}
              </div>
            </Row>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default PackageSelector;
